import {
  type FieldValues,
  type UseControllerProps,
  useController,
} from 'react-hook-form';
import { Radio, type RadioProps } from '../Inputs/Radio';
import { useRules } from './hooks';

export type RadioFieldProps<T extends FieldValues> = UseControllerProps<T> &
  Omit<RadioProps, 'defaultValue'> & {
    box?: boolean;
    required?: boolean;
    dontShowError?: boolean;
  };

/**
 * RadioField component
 *
 * @example
 * ```tsx
 * <RadioField name="field.name" />
 * ```
 *
 * @public
 * @param name - field name
 * @param control - form control
 * @param defaultValue - default value
 * @param rules - validation rules
 * @param shouldUnregister - unregister field when unmount
 * @param onChange - onChange event
 * @param value - value of the radio
 * @param required - is field required
 * @param props - other props
 */
export function RadioField<T extends FieldValues>({
  name,
  control,
  defaultValue,
  rules,
  shouldUnregister,
  value,
  required,
  dontShowError,
  ...props
}: RadioFieldProps<T>) {
  const innerRules = useRules({ required, ...rules });
  const {
    field: { value: fieldValue, ...field },
    fieldState,
  } = useController<T>({
    name,
    control,
    defaultValue,
    rules: innerRules,
    shouldUnregister,
  });

  const checked = value === fieldValue;

  return (
    <Radio
      checked={checked}
      error={dontShowError ? undefined : fieldState.error?.message}
      value={value}
      {...field}
      {...props}
    />
  );
}
